import { type ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useShell } from './ShellContext';

interface Crumb {
  readonly label: string;
  readonly to?: string;
}

/**
 * Breadcrumb trail for the top bar. Routes that need a richer trail
 * (e.g. the patient record showing the patient's name) push it via
 * `useSetBreadcrumbs(...)`; everything else falls back to a sensible
 * URL-derived default. Trail collapses gracefully on small viewports.
 */
export function Breadcrumbs(): ReactElement | null {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { breadcrumbs } = useShell();

  const trail = breadcrumbs ?? deriveFromPath(pathname, t);
  if (trail.length === 0) return null;

  return (
    <nav
      aria-label={t('shell.breadcrumbs.ariaLabel')}
      className="flex items-center gap-1 text-[12.5px] min-w-0 truncate"
    >
      {trail.map((crumb, idx) => {
        const isLast = idx === trail.length - 1;
        return (
          <span key={`${String(idx)}-${crumb.label}`} className="inline-flex items-center gap-1">
            {idx > 0 && (
              <ChevronRight
                className="h-3 w-3 text-white/40 shrink-0"
                aria-hidden="true"
              />
            )}
            {!isLast && crumb.to !== undefined ? (
              <Link
                to={crumb.to}
                className="text-white/70 hover:text-white transition-colors"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                aria-current={isLast ? 'page' : undefined}
                className={
                  isLast ? 'text-white font-medium' : 'text-white/70'
                }
              >
                {crumb.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

type Translator = (key: string) => string;

function deriveFromPath(pathname: string, t: Translator): readonly Crumb[] {
  // "All Patients" is the root starting point of every trail (PTV2-1203).
  // Rendered as a plain link so nested pages can point back to the list.
  const root: Crumb = {
    label: t('shell.breadcrumbs.allPatients'),
    to: '/browse/patients',
  };
  if (pathname.startsWith('/browse/patients/mine')) {
    return [root, { label: t('routes.browsePatients.sidebar.mine') }];
  }
  // The All Patients list itself — a single, non-linked crumb.
  if (pathname === '/' || pathname === '/browse/patients') {
    return [{ label: t('shell.breadcrumbs.allPatients') }];
  }
  if (pathname.startsWith('/browse/families')) {
    return [root, { label: t('routes.browse.sidebar.families') }];
  }
  if (pathname.startsWith('/browse/questionnaires')) {
    return [root, { label: t('routes.browse.sidebar.questionnaires') }];
  }
  if (pathname.startsWith('/patients/')) {
    return [root, { label: t('shell.breadcrumbs.patient') }];
  }
  if (pathname.startsWith('/admin')) {
    return [{ label: t('shell.breadcrumbs.admin') }];
  }
  return [];
}
